import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { LOGOUT_SUCCESS } from "../../reducers/login";
import apiFetch from '../util/fetch-utils';

/* ── Tokens ─────────────────────────────────────────────────── */
const C = {
    surface: "#ffffff",
    border:  "#e2e8f0",
    text:    "#0f172a",
    muted:   "#64748b",
    hover:   "#f8fafc",
    red:     "#ef4444",
    redBg:   "#fef2f2",
    indigo:  "#6366f1",
};

/* ── Avatar button ───────────────────────────────────────────── */
const AvatarBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid transparent;
    background: #6366f1;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    font-family: "Inter", system-ui, sans-serif;
    cursor: pointer;
    transition: box-shadow 120ms, border-color 120ms;
    flex-shrink: 0;

    &:hover {
        box-shadow: 0 0 0 3px rgba(99,102,241,0.2);
    }
    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px rgba(99,102,241,0.35);
    }
`;

/* ── Dropdown ────────────────────────────────────────────────── */
const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const DropdownWrap = styled.div`
    position: relative;
    display: inline-block;
`;

const Dropdown = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    min-width: 220px;
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 12px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.12);
    z-index: 1200;
    overflow: hidden;
    animation: ${fadeIn} 140ms ease;
`;

const UserInfo = styled.div`
    padding: 14px 16px 12px;
    border-bottom: 1px solid ${C.border};
`;

const UserEmail = styled.div`
    font-size: 0.82rem;
    font-weight: 600;
    color: ${C.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const UserLabel = styled.div`
    font-size: 0.72rem;
    color: ${C.muted};
    margin-bottom: 2px;
`;

const MenuList = styled.div`
    padding: 6px;
`;

const MenuItem = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 12px;
    background: none;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-family: "Inter", system-ui, sans-serif;
    color: ${({ $danger }) => $danger ? C.red : C.text};
    cursor: pointer;
    text-align: left;
    transition: background 100ms;

    &:hover {
        background: ${({ $danger }) => $danger ? C.redBg : C.hover};
    }
`;

const Divider = styled.div`
    height: 1px;
    background: ${C.border};
    margin: 4px 6px;
`;

/* ── Logout confirm ──────────────────────────────────────────── */
const ConfirmBox = styled.div`
    padding: 14px 16px;
    border-top: 1px solid ${C.border};
`;

const ConfirmText = styled.div`
    font-size: 0.82rem;
    color: ${C.text};
    margin-bottom: 10px;
`;

const ConfirmRow = styled.div`
    display: flex;
    gap: 8px;
`;

const Btn = styled.button`
    flex: 1;
    padding: 7px 0;
    border-radius: 7px;
    border: 1px solid ${({ $primary }) => $primary ? C.red : C.border};
    background: ${({ $primary }) => $primary ? C.red : C.surface};
    color: ${({ $primary }) => $primary ? "#fff" : C.muted};
    font-size: 0.82rem;
    font-weight: 600;
    font-family: "Inter", system-ui, sans-serif;
    cursor: pointer;
    transition: opacity 120ms;
    &:hover { opacity: 0.85; }
`;

/* ── Helpers ─────────────────────────────────────────────────── */
function initials(email = "") {
    const name = email.split("@")[0] || "?";
    return name.slice(0, 2).toUpperCase();
}

/* ── Component ───────────────────────────────────────────────── */
export const AccountAvatarMenu = () => {
    const [open, setOpen]           = useState(false);
    const [confirming, setConfirming] = useState(false);
    const dispatch  = useDispatch();
    const history   = useHistory();
    const login     = useSelector((s) => s.login);
    const wrapRef   = useRef(null);

    const loggedIn = login.userDetails.isLoggedIn;
    const username = login.userDetails.username || "";

    /* close on outside click */
    useEffect(() => {
        if (!open) return;
        function onDown(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setConfirming(false);
            }
        }
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    function close() { setOpen(false); setConfirming(false); }

    function go(path) {
        history.push(path);
        close();
    }

    function logout() {
        apiFetch("/api/logout", { method: "POST" }).then(value => {
            if (value.status === 200) {
                close();
                dispatch({ type: LOGOUT_SUCCESS });
                window.location.hash = "login";
            }
        });
    }

    if (!loggedIn) {
        return (
            <AvatarBtn onClick={() => history.push("/login")} title="Sign in">
                ?
            </AvatarBtn>
        );
    }

    return (
        <DropdownWrap ref={wrapRef}>
            <AvatarBtn
                onClick={() => { setOpen(o => !o); setConfirming(false); }}
                title={username}
                aria-expanded={open}
                aria-haspopup="true"
            >
                {initials(username)}
            </AvatarBtn>

            {open && (
                <Dropdown>
                    <UserInfo>
                        <UserLabel>Signed in as</UserLabel>
                        <UserEmail title={username}>{username}</UserEmail>
                    </UserInfo>

                    <MenuList>
                        <MenuItem onClick={() => go("/account")}>My account</MenuItem>
                        <MenuItem onClick={() => go("/account/subscription")}>Subscription</MenuItem>
                        <Divider />
                        <MenuItem $danger onClick={() => setConfirming(true)}>Log out</MenuItem>
                    </MenuList>

                    {confirming && (
                        <ConfirmBox>
                            <ConfirmText>Are you sure you want to log out?</ConfirmText>
                            <ConfirmRow>
                                <Btn $primary onClick={logout}>Log out</Btn>
                                <Btn onClick={() => setConfirming(false)}>Cancel</Btn>
                            </ConfirmRow>
                        </ConfirmBox>
                    )}
                </Dropdown>
            )}
        </DropdownWrap>
    );
};

export default AccountAvatarMenu;
